// @flow
/* -----------------Globals--------------- */
import React from 'react';

/* -----------------Next js--------------- */
import dynamic from 'next/dynamic';

/* -----------------UI--------------- */
import {
  Typography,
  Grid,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

/* -----------------Utils--------------- */
import { isEmpty } from 'common/simpleUtils';
import {
  normalizeName,
} from 'common/content/normalize';

/* -----------------Child components--------------- */
import type {
  Fact,
} from 'common/types';
import ZoomImage from './ZoomImage';

const RenderMarkdown = dynamic(() => import('@components/common/RenderMarkdown'), { ssr: false });

/* -----------------Styles--------------- */
const styles = theme => ({
  gridContainer: {
    flex: 1,
    fontSize: 14,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(3),
    },
  },
  factBox: {
    fontSize: 15,
    position: 'relative',
    '& p': {
      margin: 0,
      //   display: 'inline',
    },
  },
  prompt: {
    color: theme.palette.type === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
    fontSize: 11,
    marginBottom: -4,
  },
  factItem: {
    display: 'list-item',
    lineHeight: '1.6em',
    paddingLeft: '0px !important',
    '&::marker': {
      content: '"●   "',
    },
  },
  imageThumbnail: {
    maxWidth: '100%',
    height: 100,
    [theme.breakpoints.down('xs')]: {
      height: 75,
    },
  },
});

/* -----------------Type declarations--------------- */
type Classes = {
  factContent: string,
  factLi: string,
  prompt: string,
  promptLabel: string,
  factBox: string,
  chipLabelSmall: string,
  chipRoot: string,
  imageThumbnail: string,
  gridContainer: string,
  factItem: string
};

type FactProp = {
  fact: Fact
}

/* -----------------Props--------------- */
type Props = {
    classes: Classes;
    facts: FactProp[],
};

/* -----------------Component--------------- */
const Facts = (props: Props) => {
  const {
    facts,
    classes,
  } = props;

  return (
    <Grid
      container
      direction="column"
      spacing={2}
      classes={{ root: classes.gridContainer }}
    >
      {facts.map(({
        fact: {
          image,
          id,
          name,
          prompt,
        },
      }) => {
        const finalName = normalizeName(name);
        return (
          <Grid
            item
            key={id}
            classes={{ root: classes.factItem }}
            container
            direction="column"
          >
            {image && !isEmpty(image.url)
              ? (
                <Grid item>
                  <ZoomImage
                    image={image}
                    thumbnailStyle={classes.imageThumbnail}
                  />
                </Grid>
              ) : null}
            {name || prompt ? (
              <Grid item classes={{ root: classes.factBox }}>
                {!isEmpty(prompt)
                  ? (
                    <Typography
                      variant="overline"
                      display="block"
                      classes={{ root: classes.prompt }}
                    >
                      {prompt}
                    </Typography>
                  ) : null
                      }
                {!isEmpty(finalName)
                  ? (
                    <RenderMarkdown
                      variant="fact"
                      source={finalName}
                    />
                  )
                  : null}
              </Grid>
            ) : null}
          </Grid>
        );
      })}
    </Grid>
  );
};

export default withStyles(styles)(Facts);
